import { useState, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Search, Upload, ChevronLeft, ChevronRight } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const PAGE = 50;
const SOURCE_LABELS = { milestone_api: "Milestone ERP API", excel_upload: "Excel upload", initial_seed: "initial import" };
const fmtWhen = (iso) => new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

export const ErpCodesTable = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [uploading, setUploading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["erp-items", q, page],
    queryFn: async () => (await api.get("/erp-items", { params: { q, skip: page * PAGE, limit: PAGE } })).data,
    keepPreviousData: true,
  });
  const { data: sync } = useQuery({
    queryKey: ["erp-sync-status"],
    queryFn: async () => (await api.get("/erp-items/sync-status")).data,
  });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: res } = await api.post("/erp-items/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`ERP codes updated — ${res.created} new, ${res.updated} changed (${res.total_in_file} in file).`);
      qc.invalidateQueries({ queryKey: ["erp-items"] });
      qc.invalidateQueries({ queryKey: ["erp-sync-status"] });
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const items = data?.items || [];
  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <div data-testid="erp-codes-panel">
      {sync?.latest && (
        <div className="flex flex-wrap items-center gap-2 mb-3 text-xs" data-testid="erp-sync-indicator">
          <span className={`h-2 w-2 rounded-full ${sync.last_api_sync ? "bg-emerald-500" : "bg-amber-400"}`} />
          <span className="text-slate-600">
            Catalog last updated <b>{fmtWhen(sync.latest.at)}</b> via {SOURCE_LABELS[sync.latest.source] || sync.latest.source}
            {typeof sync.latest.created === "number" && <> — <b>{sync.latest.created}</b> new, <b>{sync.latest.updated || 0}</b> updated{sync.latest.failed ? <span className="text-red-500">, {sync.latest.failed} failed</span> : null}</>}
            {sync.latest.by ? ` · by ${sync.latest.by}` : ""}
          </span>
          <span className={`px-2 py-0.5 rounded-full font-medium ${sync.last_api_sync ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="erp-sync-api-badge">
            {sync.last_api_sync ? `Milestone API live · last push ${fmtWhen(sync.last_api_sync.at)}` : "Milestone API not connected yet"}
          </span>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }}
                 placeholder="Search stock code, description, category…" className="h-9 pl-9" data-testid="erp-codes-search" />
        </div>
        <span className="text-sm text-slate-500 ml-auto" data-testid="erp-codes-count">{total.toLocaleString("en-IN")} product codes</span>
        {user?.role === "super_admin" && (
          <>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={upload} />
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} className="gap-1.5" data-testid="erp-codes-import-btn">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Update from Excel
            </Button>
          </>
        )}
      </div>
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          {isLoading ? <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                {["Stock Code", "Description", "Category", "Family", "UOM", "HSN", "Item #", "Active"].map((h) => (
                  <TableHead key={h} className="text-xs uppercase tracking-wider text-slate-500">{h}</TableHead>
                ))}
              </TableRow></TableHeader>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.item_code} className={it.is_active ? "" : "opacity-50"} data-testid={`erp-code-row-${it.item_code}`}>
                    <TableCell><span className="font-mono text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded whitespace-nowrap">{it.stock_code}</span></TableCell>
                    <TableCell className="text-slate-700 min-w-[280px]">{it.description}</TableCell>
                    <TableCell className="text-slate-500 whitespace-nowrap">{it.category}</TableCell>
                    <TableCell className="text-slate-500 whitespace-nowrap">{it.family}</TableCell>
                    <TableCell className="text-slate-500">{it.uom}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-500">{it.hsn}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-400">{it.item_code}</TableCell>
                    <TableCell className="text-xs">{it.is_active ? <span className="text-emerald-600">Yes</span> : <span className="text-slate-400">No</span>}</TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400">No product codes match “{q}”.</TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
        </div>
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-100 text-sm text-slate-500">
          <span>Page {page + 1} of {pages}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="gap-1" data-testid="erp-codes-prev"><ChevronLeft className="h-4 w-4" /> Prev</Button>
            <Button variant="outline" size="sm" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} className="gap-1" data-testid="erp-codes-next">Next <ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
